import { FormInstance, Input as InputType } from 'antd';
import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ProjectConversationFormPayload,
  UpdateConversationFormItems,
} from 'api/project-conversations';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { Input, TextArea } from 'components/atoms/input';
import { useTranslation } from 'hooks/use-translation';

interface NewConversationFormProps {
  onSubmit: (form: FormInstance<any>) => void;
  loading?: boolean;
  name: string;
  initialValues?: Partial<ProjectConversationFormPayload>;
  hidePrivateCheckbox?: boolean;
  submitText?: React.ReactNode;
}

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const ConversationForm: FC<NewConversationFormProps> = (props) => {
  const inputRef = useRef<InputType>(null);
  const { projectId: projectIdFromRoute } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdFromRoute);

  const {
    onSubmit,
    name,
    initialValues = {
      conversationName: '',
      conversationDescription: '',
      private: false,
      addAllMemberToChannel: false,
      projectId,
    },
    loading,
    hidePrivateCheckbox,
    submitText,
  } = props;
  const [form] = Form.useForm();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const conversationName = useTranslation(
    'projects.conversations.form.channel_name',
  );
  const conversationNamePlaceholder = useTranslation(
    'projects.conversations.form.channel_name_placeholder',
  );
  const channelDescription = useTranslation(
    'projects.conversations.form.channel_description',
  );
  const channelDescriptionPlaceholder = useTranslation(
    'projects.conversations.form.channel_description_placeholder',
  );

  const requiredMessage = useTranslation('input.required');

  const maxlengthMessage = useTranslation(
    'projects.conversations.form.channel_name_length_validation',
  );

  const onFinish = () => {
    onSubmit(form);
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <Form
      form={form}
      name={name}
      autoComplete="off"
      initialValues={initialValues}
      preserve={false}
      layout="vertical"
      onFinish={onFinish}
    >
      <Form.Item
        name={UpdateConversationFormItems.ConversationName}
        label={conversationName}
        rules={[
          { required: true, message: requiredMessage },
          { max: 80, message: maxlengthMessage },
        ]}
      >
        <Input placeholder={conversationNamePlaceholder} ref={inputRef} />
      </Form.Item>
      <Form.Item
        name={UpdateConversationFormItems.ConversationDescription}
        label={channelDescription}
        help={
          <FormattedMessage id="projects.conversations.form.channel_description_hint" />
        }
      >
        <TextArea
          autoSize={{ minRows: 2, maxRows: 3 }}
          placeholder={channelDescriptionPlaceholder}
        />
      </Form.Item>
      {!hidePrivateCheckbox ? (
        <Form.Item
          name={UpdateConversationFormItems.Private}
          valuePropName="checked"
        >
          <Checkbox>
            <FormattedMessage
              id="projects.conversations.form.channel_private"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </Checkbox>
        </Form.Item>
      ) : null}
      <Form.Item>
        <Actions>
          <Button type="primary" htmlType="submit" loading={loading}>
            {submitText ?? (
              <FormattedMessage id="projects.conversations.form.channel.create" />
            )}
          </Button>
        </Actions>
      </Form.Item>
    </Form>
  );
};
